import { Box, Text, Paper, Divider } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import type { ModelViewModel, MetricCostConfig } from "@models/model";
import { useTranslation } from "@hooks/useTranslation";
import "./ModelDetailsDisplay.scss";

const renderPricingInfo = (costConfig: Record<string, MetricCostConfig>) => {
    const groupedConfigs: Record<
        string,
        Array<MetricCostConfig & { key: string }>
    > = {};

    Object.entries(costConfig).forEach(([key, config]) => {
        const baseMetric = key.replace(/_tier\d+$/, "");
        if (!groupedConfigs[baseMetric]) {
            groupedConfigs[baseMetric] = [];
        }
        groupedConfigs[baseMetric].push({ ...config, key });
    });

    return Object.entries(groupedConfigs).map(([metricType, configs]) => {
        const displayName = metricType
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
        const isTiered = configs.length > 1;

        return (
            <Box key={metricType} className="model-details-display__pricing-metric">
                <Text
                    size="xs"
                    fw={600}
                    className="model-details-display__pricing-metric-title"
                >
                    {displayName}:
                </Text>
                {isTiered ? (
                    <Box className="model-details-display__pricing-tiers">
                        {configs.map((config, index) => {
                            const tierLabel = `Tier ${index + 1}`;
                            const rangeText =
                                config.tier_start !== null && config.tier_end !== null
                                    ? `${config.tier_start.toLocaleString()} - ${config.tier_end.toLocaleString()}`
                                    : config.tier_start !== null && config.tier_end === null
                                        ? `${config.tier_start.toLocaleString()}+`
                                        : config.tier_end !== null
                                            ? `0 - ${config.tier_end.toLocaleString()}`
                                            : "All";

                            return (
                                <Text
                                    key={index}
                                    size="xs"
                                    className="model-details-display__pricing-tier"
                                >
                                    • {tierLabel} ({rangeText}): ${config.unit_cost} /{" "}
                                    {config.unit_scale.toLocaleString()} {config.unit_name}s
                                </Text>
                            );
                        })}
                    </Box>
                ) : (
                    <Text size="xs">
                        ${configs[0].unit_cost} / {configs[0].unit_scale.toLocaleString()}{" "}
                        {configs[0].unit_name}s
                    </Text>
                )}
            </Box>
        );
    });
};

interface ModelDetailsDisplayProps {
    selectedModel: ModelViewModel;
    selectedProvider: string;
}

export const ModelDetailsDisplay = ({
    selectedModel,
    selectedProvider,
}: ModelDetailsDisplayProps) => {
    const { t } = useTranslation();

    return (
        <Paper className="model-details-display" withBorder p="md">
            <Text size="sm" fw={600} mb="xs">
                {t("admin.flowConfigs.labels.modelDetails")}
            </Text>
            <Divider className="model-details-display__divider" mb="sm" />

            <Box className="model-details-display__chips">
                <Badge size="sm">
                    {selectedModel.displayName || selectedModel.modelName}
                </Badge>
                <Badge size="sm" variant="default">
                    Provider: {selectedProvider}
                </Badge>
                <Badge
                    size="sm"
                    color={selectedModel.status === "active" ? "green" : "gray"}
                >
                    {selectedModel.status}
                </Badge>
            </Box>

            {selectedModel.modelMetadata &&
                Object.keys(selectedModel.modelMetadata).length > 0 && (
                    <Box className="model-details-display__metadata">
                        <Text
                            size="xs"
                            fw={600}
                            className="model-details-display__metadata-title"
                        >
                            Capabilities:
                        </Text>
                        <Box className="model-details-display__metadata-items">
                            {selectedModel.modelMetadata.context_window && (
                                <Badge variant="outline" size="sm">
                                    Context:{" "}
                                    {(
                                        selectedModel.modelMetadata.context_window as number
                                    ).toLocaleString()}
                                </Badge>
                            )}
                            {selectedModel.modelMetadata.supports_streaming && (
                                <Badge variant="outline" size="sm">
                                    Streaming
                                </Badge>
                            )}
                            {selectedModel.modelMetadata.supports_multimodal && (
                                <Badge variant="outline" size="sm">
                                    Multimodal
                                </Badge>
                            )}
                            {selectedModel.modelMetadata.supports_function_calling && (
                                <Badge variant="outline" size="sm">
                                    Function Calling
                                </Badge>
                            )}
                        </Box>
                    </Box>
                )}

            {selectedModel.costInfo && (
                <Box className="model-details-display__cost-info">
                    <Text
                        size="xs"
                        fw={600}
                        className="model-details-display__cost-title"
                    >
                        Pricing ({selectedModel.costInfo.currency}):
                    </Text>
                    <Box className="model-details-display__cost-items">
                        {renderPricingInfo(selectedModel.costInfo.costConfig)}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};
